import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/student-report/StudentReportAction";
import {
  DeleteButton,
  ExportButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { RegistrationCancellationReportFilter } from "./registration-cancellation-report-filter/RegistrationCancellationReportFilter";
import { RegistrationCancellationReportTable } from "./registration-cancellation-report-table/RegistrationCancellationReportTable";
import { useRegistrationCancellationReportUIContext } from "./RegistrationCancellationReportUIContext";

export function RegistrationCancellationReportCard() {
  // Registration Cancellation Report UI Context
  const cancellationReportUIContext = useRegistrationCancellationReportUIContext();
  const cancellationReportUIProps = useMemo(() => {
    return {
      ids: cancellationReportUIContext.ids,
      setIds: cancellationReportUIContext.setIds,
      columns: cancellationReportUIContext.columns,
      queryParams: cancellationReportUIContext.queryParams
    };
  }, [cancellationReportUIContext]);
    
  // Getting curret state of Registration Cancellation Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentReport }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;
  
  // Authority Parameters
  const { isExportationAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Registration Cancellation Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  // ...
  const exportedColumns = cancellationReportUIProps.columns.filter(column => column.dataField !== "action" && !column.hidden);
  
  /** Belli Bir Öğrenci Kayıt İptal Raporunu Excele Aktarır */
  const exportRegistrationCancellationReportToExcel = () => {
    dispatch(actions.getAllRegistrationCancellationReport(cancellationReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = exportedColumns.map(column => column.width);
      const columns = exportedColumns.map(column => column.text);
      const fields = exportedColumns.map(column => column.dataField);

      entities.forEach(entity => {
        const row = [];

        fields.forEach(field => {
          const propArr = field.split('.');
          if (propArr.length === 1) row.push(entity[propArr[0]]);
          else if (propArr.length === 2) row.push(entity[propArr[0]][propArr[1]]);
        });
        
        rows.push(row);
      })
      
      const filename = "Kayıt İptal Raporu";
      exportToExcel({ columns, rows, widths, filename });
    });
  }

  /** Belli Bir Öğrenci Kayıt İptal Raporunu PDF e Aktarır */
  const exportRegistrationCancellationReportToPDF = () => {
    dispatch(actions.getAllRegistrationCancellationReport(cancellationReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [];

      exportedColumns.forEach(column => {
        const propArr = column.dataField.split('.');
        const view = {
          header: column.text,
          rows: entities.map(entity => {
            if (propArr.length === 1) return entity[propArr[0]];
            else if (propArr.length === 2) return entity[propArr[0]][propArr[1]];
          })
        };
        views.push(view);
      });
  
      const filename = "Kayıt İptal Raporu";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğrenci Kayıt İptal Raporunu Yazdırma Penceresini Açar */
  const printRegistrationCancellationReport = () => {
    dispatch(actions.getAllRegistrationCancellationReport(cancellationReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Kayıt İptal Raporu";
      const properties = exportedColumns.map(column => column.text);
  
      entities.forEach(entity => {
        const row = {};

        exportedColumns.forEach(column => {
          const propArr = column.dataField.split('.');
          if (propArr.length === 1) row[column.text] = entity[propArr[0]] ?? "";
          else if (propArr.length === 2) row[column.text] = entity[propArr[0]][propArr[1]] ?? "";
        });

        data.push(row);
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Öğrenci Kayıt İptal Raporunu Panoya Kopyalar */
  const copyRegistrationCancellationReport = () => {
    dispatch(actions.getAllRegistrationCancellationReport(cancellationReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = exportedColumns.map(column => column.text);
      const fields = exportedColumns.map(column => column.dataField);
      const header = "Kayıt İptal Raporu";
      
      entities.forEach(entity => {
        const row = [];

        fields.forEach(field => {
          const propArr = field.split('.');
          if (propArr.length === 1) row.push(entity[propArr[0]] ?? "");
          else if (propArr.length === 2) row.push(entity[propArr[0]][propArr[1]] ?? "");
        });

        rows.push(row);
      });

      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Öğrencinin Belli Bir Dönemdeki Kaydını Aktif Eder */
  const activateRegistration = (term, studentId) => {    
    dispatch(actions.activateRegistrations(term, studentId)).then(result => onResult(result));
  }

  /** Belli Bir Öğrenci Listesini Siler */
  const deleteStudents = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");  
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteStudents(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        cancellationReportUIProps.setIds([]);
        dispatch(actions.getRegistrationCancellationReport(cancellationReportUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Kayıt İptal Raporu{cancellationReportUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{cancellationReportUIProps.ids.length}</span>]</> :
                  ""}
                </>;
  
  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}      
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportRegistrationCancellationReportToExcel}
                exportToPDF={exportRegistrationCancellationReportToPDF}
                print={printRegistrationCancellationReport}
                copy={copyRegistrationCancellationReport}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deleteStudents(cancellationReportUIProps.ids)}
              />
            }
          </CardHeaderToolbar>          
        }
      </CardHeader>
      <CardBody>
        <RegistrationCancellationReportFilter />
        <RegistrationCancellationReportTable
          activateRegistration={activateRegistration}
          deleteStudents={deleteStudents}
        />
      </CardBody>
    </Card>
  );
}